import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Feature, Geometry, LineString, MultiLineString } from 'geojson';
import type { Layer } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import { useMapColor } from '../useMapColor';
import { useAggregationConfig } from '../useAggregationConfig';
import { useMapVisibility } from '../useMapVisibility';
import { useMapSelection } from '../useMapSelection';
import { useLinkAggregationData } from '../../data/useLinkAggregationData';
import { useLinksData } from '../../data/useLinksData';
import { useDataFilter } from '../useDataFilter';
import { useLegendConfig } from '../useLegendConfig';
import { useMetaData } from '../../data/useMetaData';
import { useMapColorPalette } from '../useMapColorPalette';
import { useCustomAreaCreation } from '../useCustomAreaCreation';
import { useCustomAreaEdition } from '../useCustomAreaEdition';
import { COLORS } from '../../../constants/color';
import type { Link } from '../../../types/domain';
import {
  bordersToRanges,
  calcBins,
  calcPercentile,
} from '../../../utils/statistics';
import { alpha, toRgb, type RgbColor } from '../../../utils/color';
import { formatCount } from '../../../utils/format';
import type { Legend } from '../../../types/common';

type CustomFeature = Feature<LineString | MultiLineString> & {
  way_id: string;
  value?: number;
  tooltip: string;
};

type UseLinkLayer = {
  linkLayer: Layer;
  linkLegends: Legend[];
};

const isCustomFeature = (feature: Feature): feature is CustomFeature => {
  return (
    'way_id' in feature &&
    typeof feature.way_id === 'string' &&
    ('value' in feature
      ? typeof feature.value === 'number' ||
        typeof feature.value === 'undefined'
      : true) &&
    'tooltip' in feature &&
    typeof feature.tooltip === 'string'
  );
};

const useLinkLayer = (): UseLinkLayer => {
  const { t } = useTranslation();
  const { dataFilter, filteredLinkWayIds } = useDataFilter();
  const { aggregationConfig } = useAggregationConfig();
  const { visibleLinkTypes, isAggregationVisible } = useMapVisibility();
  const { filteredAggregations } = useLinkAggregationData();
  const { selectionKind, selectedWayIds } = useMapSelection();
  const { links } = useLinksData();
  const { scaleType, scaleSize, customRange } = useLegendConfig();
  const { currentDataMeta } = useMetaData();
  const { mapColor } = useMapColor();
  const { editingCustomArea } = useCustomAreaEdition();
  const { creatingCustomArea } = useCustomAreaCreation();
  const { getRgbColors, mapColorOpacity } = useMapColorPalette();

  const isAggregating =
    aggregationConfig?.type === 'links' && filteredAggregations.size > 0;
  const isCarMode =
    dataFilter.longestDistanceModes.length === 1 &&
    dataFilter.longestDistanceModes[0] === 'car';
  const passengerFactor = Number(currentDataMeta?.passenger_factor ?? 1);

  const data = useMemo((): CustomFeature[] => {
    // 全種類の道路・線路のうち、表示中のものを1つの配列にまとめる
    const visibleLinks = Array.from(links.values()).filter(
      (link: Link): boolean => {
        if (visibleLinkTypes.includes('highway') && link.is_highway) {
          return true;
        }
        if (visibleLinkTypes.includes('railway') && link.is_railway) {
          return true;
        }
        if (visibleLinkTypes.includes('trunk_road') && link.is_trunk_road) {
          return true;
        }
        if (visibleLinkTypes.includes('other_road') && link.is_other_road) {
          return true;
        }
        return false;
      }
    );

    // 交通量が多い道路・線路を上に表示するためにソートする
    if (isAggregating) {
      visibleLinks.sort((link1: Link, link2: Link): number => {
        return (
          (filteredAggregations.get(link1.way_id) ?? 0) -
          (filteredAggregations.get(link2.way_id) ?? 0)
        );
      });
    }

    return visibleLinks.map((link: Link): CustomFeature => {
      const aggregation = isAggregating
        ? filteredAggregations.get(link.way_id)
        : undefined;
      const value =
        typeof aggregation === 'number'
          ? aggregation / (isCarMode ? passengerFactor : 1)
          : undefined;

      return {
        type: 'Feature',
        geometry: link.geometry,
        properties: {},
        way_id: link.way_id,
        value: value,
        tooltip:
          value !== undefined
            ? `${link.way_name || '-'}: ${t('cumulative')} ${formatCount(value, isCarMode ? t('unit.car') : t('unit.agent'))}`
            : link.way_name || '-',
      };
    });
  }, [
    links,
    filteredAggregations,
    visibleLinkTypes,
    isAggregating,
    isCarMode,
    passengerFactor,
    t,
  ]);

  const legends = useMemo((): Legend[] => {
    if (!isAggregating) {
      return [];
    }

    const linkValues = Array.from(
      filteredAggregations.values(),
      (value: number): number => {
        return value / (isCarMode ? passengerFactor : 1);
      }
    );
    const ranges =
      scaleType === 'custom'
        ? customRange
        : scaleType === 'quantize'
          ? bordersToRanges(calcBins(linkValues, scaleSize))
          : bordersToRanges(calcPercentile(linkValues, scaleSize));

    return getRgbColors(ranges.length).map(
      (color: RgbColor, index: number): Legend => {
        return {
          range: ranges[index],
          color: alpha(color, mapColorOpacity),
        };
      }
    );
  }, [
    filteredAggregations,
    scaleType,
    scaleSize,
    customRange,
    getRgbColors,
    mapColorOpacity,
    isAggregating,
    isCarMode,
    passengerFactor,
  ]);

  const linkLayer = new GeoJsonLayer({
    id: 'link-layer',
    data: data,
    getLineColor: (feature: Feature<Geometry>): RgbColor => {
      // 実際には不要であるが、型エラーを避けるために新しい変数に代入してから使用する
      const customFeature = feature;
      if (!isCustomFeature(customFeature)) {
        return toRgb(COLORS.transparent);
      }

      // 集計結果があるリンクについては、値の大小に応じた色分けを行う
      if (isAggregationVisible && customFeature.value !== undefined) {
        return (
          legends.find((legend: Legend): boolean => {
            return (
              customFeature.value !== undefined &&
              legend.range[0] <= customFeature.value &&
              customFeature.value <= legend.range[1]
            );
          })?.color ?? toRgb(COLORS.transparent)
        );
      }
      // クリックで選択されたリンクは強調表示する
      if (selectedWayIds.includes(customFeature.way_id)) {
        return toRgb('#FAFF63');
      }
      // フィルターの対象になっているリンクも強調表示する
      if (filteredLinkWayIds.includes(customFeature.way_id)) {
        return toRgb('#E89739');
      }
      return mapColor === 'light'
        ? toRgb(COLORS.transparent)
        : toRgb(alpha('#F0F0F0', 0.8));
    },
    getLineWidth: (feature: Feature<Geometry>): number => {
      // 実際には不要であるが、型エラーを避けるために新しい変数に代入してから使用する
      const customFeature = feature;
      if (!isCustomFeature(customFeature)) {
        return 0;
      }

      // 集計結果があるリンクについては強調表示する
      if (isAggregationVisible && customFeature.value !== undefined) {
        return 60;
      }
      // クリックで選択された、またはフィルターの対象になっているリンクは強調表示する
      if (
        selectedWayIds.includes(customFeature.way_id) ||
        filteredLinkWayIds.includes(customFeature.way_id)
      ) {
        return 60;
      }
      return 10;
    },
    lineWidthUnits: 'meters',
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 8,
    autoHighlight:
      !creatingCustomArea && !editingCustomArea && selectionKind !== 'area',
    highlightColor: toRgb(alpha('#848C12', 0.6)),
    pickable:
      !creatingCustomArea && !editingCustomArea && selectionKind !== 'area',
    visible: visibleLinkTypes.length > 0,
    updateTriggers: {
      getLineColor: [
        legends,
        isAggregationVisible,
        selectedWayIds,
        filteredLinkWayIds,
        mapColor,
      ],
      getLineWidth: [
        legends,
        isAggregationVisible,
        selectedWayIds,
        filteredLinkWayIds,
      ],
    },
  });

  return { linkLayer: linkLayer, linkLegends: legends };
};

export { useLinkLayer };
